import React, { useState } from 'react';
import SlButton from '@shoelace-style/shoelace/dist/react/button';
import SlInput from '@shoelace-style/shoelace/dist/react/input';
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import axios from 'axios';
import { URL } from "./App";

const css = `
  .forgot-password-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    max-width: 400px;
    margin: 0 auto;
  }

  .forgot-password-header {
    text-align: center;
    margin-bottom: 10px;
  }

  .forgot-password-header h2 {
    margin: 0;
    font-size: 24px;
    color: #333;
  }

  .forgot-password-header p {
    margin: 5px 0 0 0;
    color: #666;
    font-size: 14px;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .input-group label {
    font-weight: 500;
    color: #333;
    font-size: 14px;
  }

  .button-group {
    display: flex;
    gap: 10px;
    margin-top: 10px;
  }

  .button-group sl-button {
    flex: 1;
  }

  .success-message {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
    padding: 12px;
    border-radius: 4px;
    text-align: center;
    margin: 10px 0;
  }

  .error-message {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
    padding: 12px;
    border-radius: 4px;
    text-align: center;
    margin: 10px 0;
  }

  .info-message {
    background-color: #d1ecf1;
    border: 1px solid #bee5eb;
    color: #0c5460;
    padding: 12px;
    border-radius: 4px;
    text-align: center;
    margin: 10px 0;
    font-size: 13px;
  }

  .loading {
    opacity: 0.6;
    pointer-events: none;
  }
`;

function ForgotPassword({ onBack }) {
  const [step, setStep] = useState(1); // Step 1: Enter username/email, Step 2: Enter reset code, Step 3: Enter new password
  const [username, setUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleRequestReset = async () => {
    if (!username.trim()) {
      setMessage({ type: 'error', text: 'Please enter your username' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${URL}/api/request-password-reset`, {
        username
      });

      setMessage({ 
        type: 'success', 
        text: response.data.message || 'A reset code has been sent to your registered email. Please check your email.' 
      });
      setStep(2);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to send reset code. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!resetCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter the reset code' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${URL}/api/verify-reset-code`, {
        username,
        resetCode
      });

      setMessage({ 
        type: 'success', 
        text: response.data.message || 'Code verified! You can now reset your password.' 
      });
      setStep(3);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Invalid reset code. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setMessage({ type: 'error', text: 'Please enter a new password' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${URL}/api/reset-password`, {
        username,
        resetCode,
        newPassword
      });

      setMessage({ 
        type: 'success', 
        text: response.data.message || 'Password reset successful! You can now login with your new password.' 
      });
      
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to reset password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <style>{css}</style>
      
      <div className="forgot-password-header">
        <h2>Reset Password</h2>
        <p>Step {step} of 3</p>
      </div>

      {message.text && (
        <div className={`${message.type}-message`}>
          {message.text}
        </div>
      )}

      {step === 1 && (
        <>
          <div className="info-message">
            <sl-icon name="info-circle"></sl-icon> Enter your username to receive a password reset code
          </div>
          <div className="input-group">
            <label>Username</label>
            <SlInput 
              placeholder="Enter your username"
              value={username}
              onSlChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="button-group">
            <SlButton onClick={onBack} variant="default">
              <sl-icon name="arrow-left" slot="prefix"></sl-icon>
              Back
            </SlButton>
            <SlButton 
              onClick={handleRequestReset} 
              variant="primary"
              className={loading ? 'loading' : ''}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </SlButton>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="info-message">
            <sl-icon name="info-circle"></sl-icon> Enter the reset code sent to your email
          </div>
          <div className="input-group">
            <label>Reset Code</label>
            <SlInput 
              placeholder="Enter the 6-digit code"
              value={resetCode}
              onSlChange={(e) => setResetCode(e.target.value)}
              maxLength="6"
              disabled={loading}
            />
          </div>
          <div className="button-group">
            <SlButton 
              onClick={() => setStep(1)} 
              variant="default"
              disabled={loading}
            >
              <sl-icon name="arrow-left" slot="prefix"></sl-icon>
              Back
            </SlButton>
            <SlButton 
              onClick={handleVerifyCode} 
              variant="primary"
              className={loading ? 'loading' : ''}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </SlButton>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="info-message">
            <sl-icon name="info-circle"></sl-icon> Enter your new password
          </div>
          <div className="input-group">
            <label>New Password</label>
            <SlInput 
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onSlChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="input-group">
            <label>Confirm Password</label>
            <SlInput 
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onSlChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="button-group">
            <SlButton 
              onClick={() => setStep(2)} 
              variant="default"
              disabled={loading}
            >
              <sl-icon name="arrow-left" slot="prefix"></sl-icon>
              Back
            </SlButton>
            <SlButton 
              onClick={handleResetPassword} 
              variant="primary"
              className={loading ? 'loading' : ''}
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </SlButton>
          </div>
        </>
      )}
    </div>
  );
}

export default ForgotPassword;
